import { useEffect, useRef, useState } from 'react'
import PasswordInput from '../components/PasswordInput'
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator'
import { assessmentAccepts, passwordAssessment } from '../lib/passwordPolicy'
import type { PasswordAssessment } from '../lib/passwordPolicy'
import { hasValidRegistrationEmailDomain } from '../lib/registrationEmail'
import {
  isVerificationRequired,
  login,
  loginWithGoogle,
  register,
  resendCode,
  verifyEmail,
  requestPasswordReset,
  verifyPasswordResetCode,
  completePasswordReset,
  startAppleWebSignIn,
} from '../lib/api'

interface LoginViewProps {
  onSuccess: () => void
  // `true` when the server has SMTP/email verification enabled. False/undefined
  // hides the "Forgot password?" link from the sign-in form.
  passwordResetEnabled?: boolean
  registrationEnabled?: boolean
  appleSignInEnabled?: boolean
}

// Backend errors arrive as the raw response body (JSON like {"detail": ...}).
// detail is usually a string, but the "email not verified" 403 carries an
// object {message, verification_required, email}. Normalize both.
interface ErrorDetail {
  message?: string
  verification_required?: boolean
  email?: string
}

function parseErrorDetail(message: string): string | ErrorDetail {
  try {
    const parsed = JSON.parse(message)
    return parsed.detail ?? message
  } catch {
    return message
  }
}

function errorText(detail: string | ErrorDetail, fallback: string): string {
  if (typeof detail === 'string') return detail
  if (detail && typeof detail.message === 'string') return detail.message
  return fallback
}

interface GoogleCredentialResponse {
  credential: string
  select_by?: string
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string
            callback: (resp: GoogleCredentialResponse) => void
            nonce?: string
            auto_select?: boolean
            cancel_on_tap_outside?: boolean
          }) => void
          renderButton: (
            parent: HTMLElement,
            options: Record<string, unknown>,
          ) => void
        }
      }
    }
  }
}

const GOOGLE_CLIENT_ID = (import.meta.env.VITE_GOOGLE_OAUTH_WEB_CLIENT_ID || '') as string

function generateNonce(): string {
  // crypto.randomUUID is unavailable in non-secure contexts (plain HTTP) and
  // some embedded webviews (e.g. pywebview). Fall back to getRandomValues, then
  // Math.random, so the auth screen never crashes on mount.
  const c = globalThis.crypto
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID()
  }
  if (c && typeof c.getRandomValues === 'function') {
    const bytes = new Uint8Array(16)
    c.getRandomValues(bytes)
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  }
  return `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}${Math.random().toString(16).slice(2)}`
}

type LoginStep = 'auth' | 'verify' | 'reset-request' | 'reset-verify' | 'reset-new-password' | 'reset-complete'
const REGISTRATION_DISABLED_DETAIL = 'Registration is disabled'

export default function LoginView({
  onSuccess,
  passwordResetEnabled = false,
  registrationEnabled = true,
  appleSignInEnabled = false,
}: LoginViewProps) {
  const [isRegister, setIsRegister] = useState(false)
  const [registrationDisabledLocally, setRegistrationDisabledLocally] = useState(false)
  const effectiveRegistrationEnabled = registrationEnabled && !registrationDisabledLocally
  const effectiveIsRegister = effectiveRegistrationEnabled && isRegister
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [registrationConfirmation, setRegistrationConfirmation] = useState('')
  const [registrationConfirmationTouched, setRegistrationConfirmationTouched] = useState(false)
  const [registrationEmailTouched, setRegistrationEmailTouched] = useState(false)
  const [registrationAssessment, setRegistrationAssessment] = useState<PasswordAssessment>(passwordAssessment(''))
  const [displayName, setDisplayName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [appleLoading, setAppleLoading] = useState(false)
  const [googleReady, setGoogleReady] = useState(!GOOGLE_CLIENT_ID)
  const [googleUnavailable, setGoogleUnavailable] = useState(false)
  const [emailExpanded, setEmailExpanded] = useState(false)

  // Email-verification step (shown when the server hard-gates registration)
  // plus the password-reset sub-flow. All steps start in 'auth'.
  const [step, setStep] = useState<LoginStep>('auth')
  const [pendingEmail, setPendingEmail] = useState('')
  const [code, setCode] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [resending, setResending] = useState(false)

  // Password-reset state: resetToken is only ever held in component memory and
  // wiped on cancel/back/success. Never persisted to URL or localStorage.
  const [resetToken, setResetToken] = useState<string | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [resetConfirmationTouched, setResetConfirmationTouched] = useState(false)
  const [resetAssessment, setResetAssessment] = useState<PasswordAssessment>(passwordAssessment(''))

  const googleButtonRef = useRef<HTMLDivElement | null>(null)
  const onSuccessRef = useRef(onSuccess)
  onSuccessRef.current = onSuccess
  // One nonce per LoginView mount — fresh on each visit to the auth screen.
  const nonceRef = useRef<string>(generateNonce())

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return
    const parent = googleButtonRef.current
    if (!parent) return

    let cancelled = false
    let timedOut = false
    let retryTimer: number | undefined
    const deadline = window.setTimeout(() => {
      if (!cancelled) {
        timedOut = true
        if (retryTimer !== undefined) window.clearTimeout(retryTimer)
        setGoogleUnavailable(true)
        setGoogleReady(false)
      }
    }, 5000)
    const tryRender = () => {
      if (cancelled || timedOut) return
      const gis = window.google?.accounts?.id
      if (!gis) {
        // Script not loaded yet — retry until it is.
        retryTimer = window.setTimeout(tryRender, 150)
        return
      }
      try {
        gis.initialize({
        client_id: GOOGLE_CLIENT_ID,
        nonce: nonceRef.current,
        callback: async (resp: GoogleCredentialResponse) => {
          setError(null)
          setLoading(true)
          try {
            await loginWithGoogle(resp.credential, nonceRef.current)
            onSuccessRef.current()
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Google sign-in failed'
            const text = errorText(parseErrorDetail(message), message)
            if (text === REGISTRATION_DISABLED_DETAIL) setRegistrationDisabledLocally(true)
            setError(text)
            // After a failed attempt, regenerate the nonce so the next try is fresh.
            nonceRef.current = generateNonce()
          } finally {
            setLoading(false)
          }
        },
        })
        parent.replaceChildren()
        gis.renderButton(parent, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: effectiveRegistrationEnabled ? 'continue_with' : 'signin_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: parent.clientWidth || 320,
        })
        window.clearTimeout(deadline)
        setGoogleReady(true)
        setGoogleUnavailable(false)
      } catch {
        timedOut = true
        window.clearTimeout(deadline)
        setGoogleReady(false)
        setGoogleUnavailable(true)
      }
    }
    tryRender()
    return () => {
      cancelled = true
      window.clearTimeout(deadline)
      if (retryTimer !== undefined) window.clearTimeout(retryTimer)
    }
  }, [effectiveRegistrationEnabled])

  const goToVerify = (forEmail: string) => {
    setPendingEmail(forEmail)
    setCode('')
    setError(null)
    setNotice(null)
    setStep('verify')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (loading) return
    if (effectiveIsRegister) {
      setRegistrationEmailTouched(true)
      setRegistrationConfirmationTouched(true)
      if (
        !hasValidRegistrationEmailDomain(email)
        || !assessmentAccepts(registrationAssessment, password)
        || password !== registrationConfirmation
      ) return
    } else if (!password) {
      return
    }
    setLoading(true)

    try {
      if (effectiveIsRegister) {
        const result = await register(email.trim(), password, displayName)
        if (isVerificationRequired(result)) {
          goToVerify(result.email)
          return
        }
      } else {
        await login(email, password)
      }
      onSuccess()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Authentication failed'
      const detail = parseErrorDetail(message)
      // An unverified account trying to log in gets routed to the code screen.
      if (typeof detail === 'object' && detail.verification_required) {
        goToVerify(detail.email || email)
        return
      }
      const text = errorText(detail, message)
      if (effectiveIsRegister && text === REGISTRATION_DISABLED_DETAIL) {
        setRegistrationDisabledLocally(true)
      }
      setError(text)
    } finally {
      setLoading(false)
    }
  }

  const handleAppleSignIn = async () => {
    if (loading || appleLoading) return
    setError(null)
    setAppleLoading(true)
    try {
      await startAppleWebSignIn()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Apple sign-in failed'
      const text = errorText(parseErrorDetail(message), message)
      if (text === REGISTRATION_DISABLED_DETAIL) setRegistrationDisabledLocally(true)
      setError(text)
      setAppleLoading(false)
    }
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!effectiveRegistrationEnabled) return
    setError(null)
    setLoading(true)
    try {
      await verifyEmail(pendingEmail, code.trim())
      onSuccess()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Verification failed'
      const text = errorText(parseErrorDetail(message), message)
      if (text === REGISTRATION_DISABLED_DETAIL) setRegistrationDisabledLocally(true)
      setError(text)
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    if (!effectiveRegistrationEnabled) return
    setError(null)
    setNotice(null)
    setResending(true)
    try {
      await resendCode(pendingEmail)
      setNotice('If your email needs confirming, a new code is on its way.')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not resend the code'
      const text = errorText(parseErrorDetail(message), message)
      if (text === REGISTRATION_DISABLED_DETAIL) setRegistrationDisabledLocally(true)
      setError(text)
    } finally {
      setResending(false)
    }
  }

  const backToAuth = () => {
    setStep('auth')
    setCode('')
    setError(null)
    setNotice(null)
  }

  // Begin the password-reset flow. Always pre-fill the email from the sign-in
  // screen when the user has typed one — the request endpoint is
  // enumeration-safe so a wrong address won't disclose account existence.
  const startResetFlow = () => {
    if (pendingEmail !== email) setPendingEmail(email)
    setCode('')
    setError(null)
    setNotice(null)
    setResetToken(null)
    setNewPassword('')
    setConfirmPassword('')
    setResetConfirmationTouched(false)
    setResetAssessment(passwordAssessment(''))
    setStep('reset-request')
  }

  const handleResetRequest = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setNotice(null)
    setLoading(true)
    try {
      await requestPasswordReset(pendingEmail)
      setStep('reset-verify')
      setNotice('If a password account exists for this email, we sent a code.')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not request a reset code'
      setError(errorText(parseErrorDetail(message), message))
    } finally {
      setLoading(false)
    }
  }

  const handleResetVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setNotice(null)
    setLoading(true)
    try {
      const result = await verifyPasswordResetCode(pendingEmail, code.trim())
      setResetToken(result.reset_token)
      setCode('')
      setStep('reset-new-password')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not verify the code'
      setError(errorText(parseErrorDetail(message), message))
    } finally {
      setLoading(false)
    }
  }

  const handleResetResend = async () => {
    setError(null)
    setNotice(null)
    setResending(true)
    try {
      await requestPasswordReset(pendingEmail)
      setNotice('If your email needs a code, another one is on its way.')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not resend the code'
      setError(errorText(parseErrorDetail(message), message))
    } finally {
      setResending(false)
    }
  }

  const handleResetComplete = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setResetConfirmationTouched(true)
    if (loading || !assessmentAccepts(resetAssessment, newPassword) || newPassword !== confirmPassword) return
    if (!resetToken) {
      setError('Your reset session expired. Please start over.')
      setStep('reset-request')
      return
    }
    setLoading(true)
    try {
      await completePasswordReset(resetToken, newPassword)
      // Success — wipe secrets from memory and show confirmation. The user
      // navigates back to sign-in via a button so they explicitly opt in.
      setResetToken(null)
      setNewPassword('')
      setConfirmPassword('')
      setResetConfirmationTouched(false)
      setResetAssessment(passwordAssessment(''))
      setNotice(null)
      setError(null)
      setStep('reset-complete')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not reset the password'
      setError(errorText(parseErrorDetail(message), message))
    } finally {
      setLoading(false)
    }
  }

  // Any user-initiated cancel/back from a reset screen must wipe the secrets
  // currently held in memory before re-rendering the previous step.
  const cancelReset = () => {
    setResetToken(null)
    setNewPassword('')
    setConfirmPassword('')
    setResetConfirmationTouched(false)
    setResetAssessment(passwordAssessment(''))
    setCode('')
    setError(null)
    setNotice(null)
    setStep('auth')
  }

  if (step === 'reset-request') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-slate-900">Reset your password</h1>
              <p className="mt-1 text-sm text-slate-500">
                Enter the email on your account and we'll send a 6-digit code.
              </p>
            </div>

            <form onSubmit={handleResetRequest} className="space-y-4">
              <div>
                <label htmlFor="reset-email" className="block text-sm font-medium text-slate-700 mb-1">
                  Email
                </label>
                <input
                  id="reset-email"
                  type="email"
                  required
                  value={pendingEmail}
                  onChange={(e) => setPendingEmail(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="you@example.com"
                />
              </div>

              {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || pendingEmail.length < 3}
                className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Sending code...' : 'Send reset code'}
              </button>
            </form>

            <div className="mt-6 text-left">
              <button
                type="button"
                onClick={cancelReset}
                className="text-sm text-slate-500 hover:text-slate-700"
              >
                &larr; Back to sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (step === 'reset-verify') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-slate-900">Enter your code</h1>
              <p className="mt-1 text-sm text-slate-500">
                We sent a 6-digit code to{' '}
                <span className="font-medium text-slate-700">{pendingEmail}</span>.
              </p>
            </div>

            <form onSubmit={handleResetVerify} className="space-y-4">
              <div>
                <label htmlFor="reset-code" className="block text-sm font-medium text-slate-700 mb-1">
                  Verification code
                </label>
                <input
                  id="reset-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-center text-2xl font-semibold tracking-[0.4em] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="000000"
                />
              </div>

              {notice && (
                <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-700">
                  {notice}
                </div>
              )}

              {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || code.length < 6}
                className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Verifying...' : 'Verify code'}
              </button>
            </form>

            <div className="mt-6 flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => { setCode(''); setError(null); setNotice(null); setStep('reset-request') }}
                className="text-slate-500 hover:text-slate-700"
              >
                &larr; Back
              </button>
              <button
                type="button"
                onClick={handleResetResend}
                disabled={resending}
                className="text-blue-600 hover:text-blue-700 disabled:opacity-50"
              >
                {resending ? 'Sending...' : 'Send another code'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (step === 'reset-new-password') {
    const resetPasswordAccepted = assessmentAccepts(resetAssessment, newPassword)
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-slate-900">Choose a new password</h1>
              <p className="mt-1 text-sm text-slate-500">
                For <span className="font-medium text-slate-700">{pendingEmail}</span>.
                Use a strong password or passphrase.
              </p>
            </div>

            <form onSubmit={handleResetComplete} className="space-y-4">
              <PasswordInput
                id="reset-new-password"
                label="New password"
                required
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                aria-describedby="reset-password-policy"
                placeholder="Strong password or passphrase"
              />
              <PasswordInput
                id="reset-confirm-password"
                label="Confirm new password"
                required
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => setResetConfirmationTouched(true)}
                error={resetConfirmationTouched && newPassword !== confirmPassword ? 'New passwords do not match.' : undefined}
                placeholder="Re-enter the password"
              />
              <PasswordStrengthIndicator
                password={newPassword}
                confirmation={confirmPassword}
                confirmationTouched={resetConfirmationTouched}
                active={step === 'reset-new-password'}
                describedById="reset-password-policy"
                onAssessment={setResetAssessment}
              />

              {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700" role="alert">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={
                  loading
                  || !resetPasswordAccepted
                  || newPassword !== confirmPassword
                }
                className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Saving...' : 'Update password'}
              </button>
            </form>

            <div className="mt-6 text-left">
              <button
                type="button"
                onClick={() => {
                  setResetToken(null)
                  setNewPassword('')
                   setConfirmPassword('')
                   setResetConfirmationTouched(false)
                   setResetAssessment(passwordAssessment(''))
                  setCode('')
                  setError(null)
                  setNotice(null)
                  setStep('reset-request')
                }}
                className="text-sm text-slate-500 hover:text-slate-700"
              >
                &larr; Start over
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (step === 'reset-complete') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-slate-900">Password updated</h1>
              <p className="mt-1 text-sm text-slate-500">
                Your Constituent password has been reset. Sign in with your new password.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                // Drop any leftover reset state and return to the sign-in form.
                // Preserve the email so the user doesn't have to retype it; clear
                // any other secret fields they may have left behind.
                setResetToken(null)
                setNewPassword('')
                setConfirmPassword('')
                setPassword('')
                setCode('')
                setError(null)
                setNotice(null)
                setStep('auth')
              }}
              className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
            >
              Sign in
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (step === 'verify' && effectiveRegistrationEnabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-slate-900">Confirm your email</h1>
              <p className="text-sm text-slate-500 mt-1">
                Enter the 6-digit code we sent to{' '}
                <span className="font-medium text-slate-700">{pendingEmail}</span>.
              </p>
            </div>

            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-slate-700 mb-1">
                  Verification code
                </label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-center text-2xl font-semibold tracking-[0.4em] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="000000"
                />
              </div>

              {notice && (
                <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-700">
                  {notice}
                </div>
              )}

              {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || code.length < 6}
                className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Verifying...' : 'Verify & continue'}
              </button>
            </form>

            <div className="mt-6 flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={backToAuth}
                className="text-slate-500 hover:text-slate-700"
              >
                &larr; Back
              </button>
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="text-blue-600 hover:text-blue-700 disabled:opacity-50"
              >
                {resending ? 'Sending...' : 'Resend code'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const registrationDomainValid = hasValidRegistrationEmailDomain(email)
  const registrationPasswordAccepted = assessmentAccepts(registrationAssessment, password)
  const authCanSubmit = !loading && (effectiveIsRegister
    ? registrationDomainValid
      && registrationPasswordAccepted
      && password === registrationConfirmation
    : password.length > 0)
  const collapseEmailOnCompact = GOOGLE_CLIENT_ID && googleReady && !googleUnavailable && !effectiveIsRegister

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-slate-900">Constituent</h1>
            <p className="text-sm text-slate-500 mt-1">
              {effectiveIsRegister ? 'Create your account' : 'Sign in to your account'}
            </p>
          </div>

          {GOOGLE_CLIENT_ID && (
            <div className="mb-6">
              <div ref={googleButtonRef} className="flex justify-center" />
              {googleUnavailable && <p className="mt-2 text-center text-xs text-amber-700">Google sign-in is unavailable. Use email below.</p>}
            </div>
          )}

          {appleSignInEnabled && <div className="mb-6 space-y-3">
            <button
              type="button"
              onClick={() => { void handleAppleSignIn() }}
              disabled={loading || appleLoading}
              className="flex min-h-11 w-full items-center justify-center rounded-lg bg-black px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2"
              aria-label="Continue with Apple"
            >
              {appleLoading ? 'Opening Apple...' : 'Continue with Apple'}
            </button>
            <div className="my-4 flex items-center gap-3 text-xs text-slate-400">
              <span className="h-px flex-1 bg-slate-200" />
              <span>or</span>
              <span className="h-px flex-1 bg-slate-200" />
            </div>
          </div>}

          {collapseEmailOnCompact && !emailExpanded && (
            <button type="button" onClick={() => setEmailExpanded(true)} className="mb-4 hidden min-h-12 w-full rounded-lg border border-slate-300 px-4 text-sm font-semibold text-slate-700 max-[599px]:block">
              Use email instead
            </button>
          )}

          <form onSubmit={handleSubmit} className={`space-y-4 ${collapseEmailOnCompact && !emailExpanded ? 'max-[599px]:hidden compact:block' : ''}`}>
            {effectiveIsRegister && (
              <div>
                <label htmlFor="displayName" className="block text-sm font-medium text-slate-700 mb-1">
                  Display Name
                </label>
                <input
                  id="displayName"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Your name"
                />
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => { if (effectiveIsRegister) setRegistrationEmailTouched(true) }}
                aria-describedby={effectiveIsRegister && registrationEmailTouched && !registrationDomainValid ? 'registration-email-error' : undefined}
                aria-invalid={effectiveIsRegister && registrationEmailTouched && !registrationDomainValid}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="you@example.com"
              />
              {effectiveIsRegister && registrationEmailTouched && !registrationDomainValid && (
                <p id="registration-email-error" role="alert" className="mt-1 text-xs text-rose-700">
                  Enter an address with a valid-looking domain, such as name@example.com.
                </p>
              )}
            </div>

            <div className="space-y-3">
              <PasswordInput
                id="password"
                label="Password"
                required
                autoComplete={effectiveIsRegister ? 'new-password' : 'current-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-describedby={effectiveIsRegister ? 'registration-password-policy' : undefined}
                placeholder={effectiveIsRegister ? 'Strong password or passphrase' : 'Your password'}
              />
              {effectiveIsRegister && (
                <>
                  <PasswordInput
                    id="registration-confirm-password"
                    label="Confirm password"
                    required
                    autoComplete="new-password"
                    value={registrationConfirmation}
                    onChange={(e) => setRegistrationConfirmation(e.target.value)}
                    onBlur={() => setRegistrationConfirmationTouched(true)}
                    error={registrationConfirmationTouched && password !== registrationConfirmation ? 'Passwords do not match.' : undefined}
                  />
                  <PasswordStrengthIndicator
                    password={password}
                    confirmation={registrationConfirmation}
                    confirmationTouched={registrationConfirmationTouched}
                    active={effectiveIsRegister && step === 'auth'}
                    describedById="registration-password-policy"
                    onAssessment={setRegistrationAssessment}
                  />
                </>
              )}
              {!effectiveIsRegister && passwordResetEnabled && (
                <div className="mt-1 flex justify-end">
                  <button
                    type="button"
                    onClick={startResetFlow}
                    className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={!authCanSubmit}
              className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Please wait...' : effectiveIsRegister ? 'Create Account' : 'Sign In'}
            </button>
          </form>

          {/* Sign-in-wrap consent notice. Covers every auth path on this card
              (email sign-in, email register, and the Google button), so it uses
              "continuing" rather than "signing in". */}
          <p className="mt-4 text-center text-xs text-slate-500">
            By continuing, you agree to our{' '}
            <a
              href="https://www.constituent.dev/terms-of-service.html"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Terms of Service (opens in new tab)"
              className="text-blue-600 hover:text-blue-700 hover:underline"
            >
              Terms of Service
              <span className="sr-only">(opens in new tab)</span>
            </a>{' '}
            and{' '}
            <a
              href="https://www.constituent.dev/privacy-policy.html"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Privacy Policy (opens in new tab)"
              className="text-blue-600 hover:text-blue-700 hover:underline"
            >
              Privacy Policy
              <span className="sr-only">(opens in new tab)</span>
            </a>
            .
          </p>

          {effectiveRegistrationEnabled && <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => {
                setIsRegister(!isRegister)
                setEmailExpanded(true)
                setPassword('')
                setRegistrationConfirmation('')
                setRegistrationConfirmationTouched(false)
                setRegistrationEmailTouched(false)
                setRegistrationAssessment(passwordAssessment(''))
                setError(null)
              }}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              {effectiveIsRegister ? 'Already have an account? Sign in' : "Don't have an account? Register"}
            </button>
          </div>}
        </div>
      </div>
    </div>
  )
}
